import React from 'react';
import PlacesAutocompleteInput from './PlacesAutocompleteInput';
import { MapLocation } from '../types';
import { MapPin, Plus, Trash2, AlertTriangle, InfoIcon, Truck, Package } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface AdditionalDestinationsInputProps {
  additionalDestinations: { 
    address: string; 
    location: MapLocation | null;
    loadStatus?: 'loaded' | 'empty';
  }[];
  setAdditionalDestinations: React.Dispatch<React.SetStateAction<{ 
    address: string; 
    location: MapLocation | null;
    loadStatus?: 'loaded' | 'empty';
  }[]>>;
  maxDestinations?: number;
  className?: string;
  firstSegmentLoadStatus?: 'loaded' | 'empty';
  setFirstSegmentLoadStatus?: (status: 'loaded' | 'empty') => void;
  countryCode?: string | null; // ISO 3166-1 alpha-2 country code for geocoding restrictions
}

const AdditionalDestinationsInput: React.FC<AdditionalDestinationsInputProps> = ({
  additionalDestinations,
  setAdditionalDestinations,
  maxDestinations = 3,
  className = "",
  firstSegmentLoadStatus = 'loaded',
  setFirstSegmentLoadStatus,
  countryCode = null
}) => {
  const { t } = useLanguage();
  
  // Add a new destination
  const handleAddDestination = () => {
    if (additionalDestinations.length < maxDestinations) {
      setAdditionalDestinations([
        ...additionalDestinations,
        { address: '', location: null, loadStatus: 'loaded' }
      ]);
    }
  };
  
  // Remove a destination
  const handleRemoveDestination = (index: number) => {
    setAdditionalDestinations(
      additionalDestinations.filter((_, i) => i !== index)
    );
  };
  
  // Update a destination's address
  const handleDestinationChange = (index: number, address: string) => {
    const newDestinations = [...additionalDestinations];
    newDestinations[index].address = address;
    setAdditionalDestinations(newDestinations);
  };
  
  // Update a destination's location
  const handlePlaceSelected = (index: number, place: google.maps.places.PlaceResult) => {
    if (place.geometry && place.geometry.location) {
      console.log('Additional destination place selected at index', index, place.formatted_address);
      const newDestinations = [...additionalDestinations];
      newDestinations[index].location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      newDestinations[index].address = place.formatted_address || newDestinations[index].address;
      setAdditionalDestinations(newDestinations);
    } else {
      console.warn('Additional destination place selected but no geometry found:', place);
    }
  };

  // Update load status for a specific segment
  const handleLoadStatusChange = (index: number, status: 'loaded' | 'empty') => {
    const newDestinations = [...additionalDestinations];
    newDestinations[index].loadStatus = status;
    setAdditionalDestinations(newDestinations);
  };
  
  return (
    <div className={`mt-6 ${className}`}>
      <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
        <h3 className="text-sm font-medium text-gray-700 flex items-center">
          <MapPin size={16} className="mr-1.5 text-primary" />
          {t('quotationForm.waypoints.nextDestinations')}
        </h3>
      </div>
      
      {additionalDestinations.length === 0 ? (
        <div className="bg-blue-50 p-2 sm:p-3 rounded-lg text-blue-800 text-xs sm:text-sm mb-3 sm:mb-4">
          <p>
            <InfoIcon size={16} className="inline-block mr-1.5" />
            {t('quotationForm.waypoints.noDestinations')}
          </p>
          <p className="mt-2 text-blue-700 text-xs italic">
            {t('quotationForm.waypoints.routeDescription')}
          </p>
        </div>
      ) : (
        <div className="mb-3 sm:mb-4 text-xs text-gray-600 italic">
          {t('quotationForm.waypoints.routeDescription')}
        </div>
      )}

      {/* First segment load status (Origin to Destination) */}
      {setFirstSegmentLoadStatus && (
        <div className="mb-3 sm:mb-4 bg-blue-50 p-2 sm:p-3 rounded-lg border border-blue-100">
          <h4 className="text-xs sm:text-sm font-medium text-blue-800 mb-1 sm:mb-2 flex items-center">
            <Truck size={16} className="mr-1.5" />
            {t('quotationForm.waypoints.firstSegmentLoad')}
          </h4>
          <p className="text-xs text-blue-700 mb-2 sm:mb-3">
            {t('quotationForm.loadStatusHelp')}
          </p>

          <div className="flex flex-wrap gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setFirstSegmentLoadStatus('loaded')}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm flex items-center ${
                firstSegmentLoadStatus === 'loaded'
                  ? 'bg-green-100 text-green-800 border border-green-200'
                  : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
              }`}
            >
              <Package size={14} className="mr-1.5" />
              {t('quotationForm.waypoints.loaded')}
            </button>
            
            <button
              type="button"
              onClick={() => setFirstSegmentLoadStatus('empty')}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm flex items-center ${
                firstSegmentLoadStatus === 'empty'
                  ? 'bg-amber-100 text-amber-800 border border-amber-200'
                  : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
              }`}
            >
              <Truck size={14} className="mr-1.5" />
              {t('quotationForm.waypoints.empty')}
            </button>
          </div>
        </div>
      )}
      
      {/* List of additional destinations */}
      <div className="space-y-3 sm:space-y-4 mb-3 sm:mb-4">
        {additionalDestinations.map((dest, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-2 sm:p-3 bg-white shadow-sm">
            <div className="flex items-center gap-1 sm:gap-2 mb-2 sm:mb-3">
              <div className="bg-blue-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0 text-xs sm:text-sm">
                {String.fromCharCode(67 + index)} {/* C, D, E, etc. */}
              </div>
              <div className="flex-grow">
                <h4 className="text-sm font-medium text-gray-700">
                  {t('quotationForm.waypoints.nextDestination')} {index + 1}
                </h4>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveDestination(index)}
                className="p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50"
                aria-label={t('quotationForm.waypoints.removeStop')}
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="mb-3">
              <PlacesAutocompleteInput
                id={`additional-destination-${index}`}
                value={dest.address}
                onChange={(value) => handleDestinationChange(index, value)}
                onPlaceSelected={(place) => handlePlaceSelected(index, place)}
                placeholder={`${t('quotationForm.waypoints.nextDestination')} ${index + 1}`}
                className="w-full"
                countryCode={countryCode}
              />
              {dest.location && (
                <div className="text-xs text-green-600 mt-1">
                  {t('mapComponent.locationConfirmed')} ({dest.location.lat.toFixed(4)}, {dest.location.lng.toFixed(4)})
                </div>
              )}
            </div>
            
            {/* Load status toggle for this segment */}
            <div className="mt-1 sm:mt-2 border-t border-gray-100 pt-1 sm:pt-2">
              <div className="text-xs text-gray-600 mb-1 sm:mb-1.5">
                {t('quotationForm.waypoints.loadStatusHelp', 'Select if the vehicle is loaded or empty for this segment')}
              </div>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                <button
                  type="button"
                  onClick={() => handleLoadStatusChange(index, 'loaded')}
                  className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-md text-xs flex items-center ${
                    dest.loadStatus === 'loaded'
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  <Package size={11} className="mr-1" />
                  {t('quotationForm.waypoints.loaded')}
                </button>
                
                <button
                  type="button"
                  onClick={() => handleLoadStatusChange(index, 'empty')}
                  className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-md text-xs flex items-center ${
                    dest.loadStatus === 'empty'
                      ? 'bg-amber-100 text-amber-800 border border-amber-200'
                      : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  <Truck size={11} className="mr-1" />
                  {t('quotationForm.waypoints.empty')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Add destination button */}
      {additionalDestinations.length < maxDestinations ? (
        <button
          type="button"
          onClick={handleAddDestination}
          className="flex items-center px-3 sm:px-4 py-1.5 sm:py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors text-xs sm:text-sm w-full sm:w-auto justify-center sm:justify-start"
        >
          <Plus size={14} className="mr-1 sm:mr-1.5" />
          {t('quotationForm.waypoints.addDestination')}
        </button>
      ) : (
        <div className="text-amber-600 text-xs flex items-center mt-2 sm:mt-0">
          <AlertTriangle size={12} className="mr-1 sm:mr-1.5 flex-shrink-0" />
          {t('quotationForm.waypoints.maxReached', {max: maxDestinations})}
        </div>
      )}
    </div>
  );
};

export default AdditionalDestinationsInput;